import type { QuantityUnit } from "../domain/feature";
import type { Cents } from "../domain/money";
import { defaultTradeForWork, tradeById, type BidStyle, type TradeId } from "../domain/trade";
import { AGGREGATE, ALWAYS_EXPLICIT, readsAsInstruction, summarizeScope, type SummaryGroup, type SummaryItem } from "./scopeSummary";
import { deriveManagementLines, type CategoryDetailLine } from "./categoryBudget";

/**
 * TURNING A RENOVATION INTO SOMETHING CONTRACTORS CAN PRICE (spec §68).
 *
 * The one idea this engine exists to express:
 *
 *     ROOM says WHERE the work is.
 *     TRADE says WHO would price it.
 *     They are not the same question, and a bid package groups by TRADE.
 *
 * Group by room and a plumber, an electrician, a cabinet maker and a
 * flooring crew all land on one page headed "Kitchen" that none of them can
 * quote. Group by feature and the electrician gets five sheets, one per
 * outlet. Both are wrong in the same way: they group by something that is
 * not who is doing the work.
 *
 * So: work whose trade is normally bought across a whole house — plumbing,
 * electrical, HVAC, flooring, painting — becomes ONE scope per trade with
 * the locations listed inside it. Work that is discrete enough to point at
 * — a window, a staircase, a run of cabinets — becomes its own item with a
 * Q number that matches a callout on the plan.
 *
 * Everything here is DERIVED and stored nowhere. The renovation remains the
 * single source of truth; a package is a way of reading it. What IS stored
 * is only what a person decided: an override, a note, a Q number's identity
 * and where its label sits.
 *
 * Pure: no I/O, no dates, no randomness. Same inputs, same package.
 */

/* ------------------------------------------------------------------ *
 * Inputs
 * ------------------------------------------------------------------ */

/** One piece of renovation scope, as the bid engine needs to see it. */
export interface BidFeatureInput {
  id: string;
  /** Stable identity across duplicated designs — what a Q number follows. */
  rootId: string;
  featureDefinitionId: string | null;
  name: string;
  action: string;
  roomId: string | null;
  floorId: string | null;
  quantityX100: number | null;
  quantityUnit: QuantityUnit | null;
  notes: string;
  sortOrder: number;
}

export interface BidLocationInput {
  roomId: string;
  roomName: string;
  floorId: string | null;
  floorName: string | null;
}

/**
 * What somebody decided about one piece of work, overriding the defaults.
 * Every field is optional: absent means "whatever the trade table says".
 */
export interface BidDecision {
  /** Keyed by feature rootId. */
  rootId: string;
  tradeId?: TradeId | null;
  style?: BidStyle | null;
  excluded?: boolean;
  /** Contractor-facing note for this specific item. */
  note?: string | null;
  /** Suppress the plan callout without excluding the work. */
  calloutSuppressed?: boolean;
}

/** Work the model does not know about, written by the user (§68P). */
export interface BidManualItem {
  id: string;
  tradeId: TradeId;
  title: string;
  description: string;
  /** A room, when the item is somewhere specific. */
  roomId: string | null;
  /** True when it should carry a Q number and a plan callout. */
  itemised: boolean;
  sortOrder: number;
}

/** Notes attached to a whole trade scope rather than one item. */
export interface BidTradeNote {
  tradeId: TradeId;
  note: string;
  /** Spec §80-5: this project's own wording for who performs the trade. */
  performer?: string | null;
  /** Force the whole trade one way regardless of its default. */
  style?: BidStyle | null;
  excluded?: boolean;
  /** Quote this trade together with another one (§68H). */
  combineWith?: TradeId | null;
}

/**
 * HOW A SCOPE IS SHOWN TO A CONTRACTOR (spec §76B).
 *
 * Presentation, and nothing else. All three modes cover exactly the same
 * underlying features; only the words on the page differ, and no mode can
 * add or remove a single piece of work.
 */
export type BidPresentationMode = "generalized" | "detailed" | "custom";

export interface BidPresentation {
  scopeKey: string;
  mode: BidPresentationMode;
  /** The user's own wording, when the mode is custom. */
  customText?: string | null;
  /**
   * What the covered work looked like when that wording was written, so a
   * later change can be reported rather than silently papered over (§76H).
   */
  customDigest?: string | null;
}

export interface BidPackageInput {
  features: readonly BidFeatureInput[];
  locations: readonly BidLocationInput[];
  decisions: readonly BidDecision[];
  manualItems: readonly BidManualItem[];
  tradeNotes: readonly BidTradeNote[];
  /** Q numbers already issued, by rootId — so they never shuffle (§68X). */
  assignedCallouts: Readonly<Record<string, number>>;
  /** Restrict the package to these trades (a trade-only package, §68J). */
  onlyTrades?: readonly TradeId[] | null;
  /** Per-scope presentation decisions (§76B). Absent means the default. */
  presentations?: readonly BidPresentation[];
  /**
   * User wording for a WORK ITEM, by its §78 group key (spec §79R). The
   * same wording the Renovation Budget shows — one identity, one name.
   */
  workItemLabels?: ReadonlyMap<string, string>;
  /**
   * What the work actually carries, by feature id (spec §80-9): the
   * assembly components the estimate already records. Names only — a bid
   * sheet never carries one of our figures. Absent means "we do not know",
   * which prints as silence, never as a guess.
   */
  inclusionsByFeature?: ReadonlyMap<string, readonly string[]>;
  /** Products / materials the project actually selected, by feature id. */
  specificationsByFeature?: ReadonlyMap<string, readonly string[]>;
  /** Material components deliberately EXCLUDED from the price. */
  ownerSuppliedByFeature?: ReadonlyMap<string, readonly string[]>;
  /**
   * Features whose assemblies still carry material the contractor buys.
   * An item with NONE of these and something excluded is labour-only —
   * which is the only case where "supplied by owner" is a fact rather
   * than a guess (spec §80-12).
   */
  materialFeatures?: ReadonlySet<string>;
}

/* ------------------------------------------------------------------ *
 * Output
 * ------------------------------------------------------------------ */

export interface BidQuantity {
  unit: QuantityUnit;
  /** Value × 100, as everywhere else in the domain. */
  valueX100: number;
}

export interface BidScopeLine {
  /** Which feature this came from, so provenance survives. */
  featureId: string;
  rootId: string;
  text: string;
  quantity: BidQuantity | null;
  note: string;
  /** What KIND of work this is — the summariser groups by it (§76C). */
  featureDefinitionId: string | null;
  action: string;
  /** The feature's own name, before it was phrased as an instruction. */
  name: string;
  /** True for work the user wrote by hand (§76Q). */
  manual: boolean;
}

/** What a contractor will read for this scope, and where it came from. */
export interface BidScopePresentation {
  mode: BidPresentationMode;
  /** The engine's own summary — always computed, whatever the mode. */
  generated: string;
  /** The user's wording, when they wrote one. */
  custom: string | null;
  /** What actually prints: generated, custom, or "" in detailed mode. */
  text: string;
  /** Manual items, kept as their own bullets (§76Q). */
  bullets: string[];
  /** What was collapsed, for the owner-facing expansion (§76J). */
  groups: SummaryGroup[];
  /** Identity of the covered work right now (§76H). */
  digest: string;
  /** True when a custom wording was written against different work. */
  stale: boolean;
}

export interface BidLocationBlock {
  roomId: string | null;
  /** "Floor 1 → Kitchen", or "Whole project" when it belongs nowhere. */
  label: string;
  lines: BidScopeLine[];
  /** Totals for this location, per unit. */
  quantities: BidQuantity[];
}

export interface BidScope {
  /** Stable key: the trade, or the work's root id for an item. */
  key: string;
  kind: "trade-wide" | "item";
  tradeId: TradeId;
  tradeName: string;
  performer: string;
  /** "PLUMBING — WHOLE PROJECT" or "KITCHEN WINDOW". */
  title: string;
  /** "Q-01" for an itemised scope; null for a trade-wide one. */
  callout: string | null;
  calloutNumber: number | null;
  locations: BidLocationBlock[];
  /** Totals across the whole scope, per unit. */
  quantities: BidQuantity[];
  note: string;
  featureIds: string[];
  rootIds: string[];
  /** True when the user wrote this rather than the renovation implying it. */
  manual: boolean;
  sortOrder: number;
  /** Spec §76B: how this scope is shown to a contractor. */
  presentation: BidScopePresentation;
  /**
   * SPREADSHEET-LEVEL WORK ITEMS (spec §79A/B): the same §78 management
   * identities, presented for a contractor to price — "Rebuild staircase",
   * "Finish trim", "Exterior doors (4)" — never room-by-room features and
   * never a checklist copied from any reference. Derived from THIS scope's
   * own lines; a quote's material/labor breakdown keys to item.key.
   */
  workItems: BidWorkItem[];
}

export interface BidWorkItem {
  /** Stable §78 identity: feature definition + action (or name fallback). */
  key: string;
  label: string;
  generatedLabel: string;
  featureCount: number;
  featureIds: string[];
  /**
   * Aggregated quantity, and ONLY when every member of the item states one
   * in the same unit (spec §80-17): a partial figure printed as the total
   * is worse than no figure at all. 'each' borrows the item's own noun so
   * a bid never reads "Approximate quantity: 3".
   */
  quantities: BidQuantity[];
  /** What the work includes, from the assemblies it already carries. */
  inclusions: string[];
  /** Products / materials the project selected for it. */
  specifications: string[];
  /**
   * Material the owner supplies: stated ONLY when the item buys no
   * material at all, so the contractor is genuinely pricing labour.
   */
  ownerSupplied: string[];
  /** Material excluded from an item that still buys other material. */
  excludedMaterial: string[];
  /** Notes typed on the underlying work. */
  notes: string[];
  /**
   * The features underneath, each with its own product and quantity. The
   * §78 identity is deliberately coarse — carpet and vinyl plank are both
   * "Replace Flooring | replace" — so the contractor layer needs the
   * members to split a line nobody could price as one number (§80.1-9).
   */
  members: BidWorkItemMember[];
}

export interface BidWorkItemMember {
  featureId: string;
  label: string;
  specifications: string[];
  /** What this feature's own assembly says it contains. */
  inclusions: string[];
  quantities: BidQuantity[];
}

export interface BidPackageModel {
  scopes: BidScope[];
  /** Work excluded on purpose, so the review surface can show it. */
  excluded: Array<{ rootId: string; name: string; tradeId: TradeId }>;
  /** Definitions with no trade — asking to be assigned, never guessed. */
  unassigned: Array<{ rootId: string; name: string }>;
  /** Highest Q number in this package, for issuing the next one. */
  highestCallout: number;
}

/* ------------------------------------------------------------------ *
 * Derivation
 * ------------------------------------------------------------------ */

const ACTION_VERB: Readonly<Record<string, string>> = {
  replace: "Replace",
  install: "Install",
  remove: "Remove",
  repair: "Repair",
  refinish: "Refinish",
  relocate: "Relocate",
  build: "Build",
  demolish: "Demolish",
  other: "",
};

/** What a contractor reads for one piece of work. */
function describe(feature: BidFeatureInput): string {
  const verb = ACTION_VERB[feature.action] ?? "";
  const name = feature.name.trim();
  // The name usually already reads as an instruction ("Replace Flooring",
  // "Close Door Opening") — and the instruction it uses need not match the
  // action's own verb, so any instruction word counts (§79.1).
  if (!verb || readsAsInstruction(name)) return name;
  return `${verb} ${name.toLowerCase()}`;
}

function addQuantity(totals: Map<QuantityUnit, number>, quantity: BidQuantity | null): void {
  if (!quantity) return;
  totals.set(quantity.unit, (totals.get(quantity.unit) ?? 0) + quantity.valueX100);
}

const quantityList = (totals: Map<QuantityUnit, number>): BidQuantity[] =>
  [...totals.entries()]
    .filter(([, value]) => value !== 0)
    .map(([unit, valueX100]) => ({ unit, valueX100 }))
    .sort((a, b) => a.unit.localeCompare(b.unit));

export function deriveBidPackage(input: BidPackageInput): BidPackageModel {
  const decisionByRoot = new Map(input.decisions.map((d) => [d.rootId, d]));
  const noteByTrade = new Map(input.tradeNotes.map((n) => [n.tradeId, n]));
  const locationById = new Map(input.locations.map((l) => [l.roomId, l]));
  const onlyTrades = input.onlyTrades && input.onlyTrades.length > 0 ? new Set(input.onlyTrades) : null;

  const label = (roomId: string | null): string => {
    if (!roomId) return "Whole project";
    const location = locationById.get(roomId);
    if (!location) return "Whole project";
    return location.floorName ? `${location.floorName} → ${location.roomName}` : location.roomName;
  };

  const excluded: BidPackageModel["excluded"] = [];
  const unassigned: BidPackageModel["unassigned"] = [];

  /** Trade + style for one feature, after every override. */
  const resolve = (feature: BidFeatureInput): { tradeId: TradeId; style: BidStyle } => {
    const decision = decisionByRoot.get(feature.rootId);
    // Removal work is DEMOLITION whatever is being removed (spec §74D);
    // the definition table decides everything else. A person's explicit
    // decision beats both.
    const fallback = defaultTradeForWork(feature.featureDefinitionId, feature.action);
    let tradeId = decision?.tradeId ?? fallback;
    // Combining two trades into one bid is a trade-level decision (§68H).
    const combine = noteByTrade.get(tradeId)?.combineWith;
    if (combine && combine !== tradeId) tradeId = combine;
    const trade = tradeById(tradeId);
    const style: BidStyle =
      decision?.style ?? noteByTrade.get(tradeId)?.style ?? trade?.defaultStyle ?? "trade-wide";
    return { tradeId, style };
  };

  /* ---- trade-wide scopes, keyed by trade ---- */
  const tradeWide = new Map<TradeId, BidScope>();
  /* ---- itemised scopes, one per piece of work ---- */
  const items: BidScope[] = [];

  const nextCallout = (() => {
    let highest = Object.values(input.assignedCallouts).reduce((m, n) => Math.max(m, n), 0);
    return (rootId: string): number => {
      const existing = input.assignedCallouts[rootId];
      if (existing) return existing;
      highest += 1;
      return highest;
    };
  })();

  const sorted = [...input.features].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));

  for (const feature of sorted) {
    const decision = decisionByRoot.get(feature.rootId);
    if (decision?.excluded) {
      excluded.push({ rootId: feature.rootId, name: feature.name, tradeId: resolve(feature).tradeId });
      continue;
    }
    const { tradeId, style } = resolve(feature);
    if (noteByTrade.get(tradeId)?.excluded) {
      excluded.push({ rootId: feature.rootId, name: feature.name, tradeId });
      continue;
    }
    if (onlyTrades && !onlyTrades.has(tradeId)) continue;
    if (tradeId === "other" && !decision?.tradeId) {
      unassigned.push({ rootId: feature.rootId, name: feature.name });
    }

    const trade = tradeById(tradeId);
    const quantity: BidQuantity | null =
      feature.quantityX100 !== null && feature.quantityUnit !== null
        ? { unit: feature.quantityUnit, valueX100: feature.quantityX100 }
        : null;
    const line: BidScopeLine = {
      featureId: feature.id,
      rootId: feature.rootId,
      text: describe(feature),
      quantity,
      note: decision?.note ?? feature.notes ?? "",
      featureDefinitionId: feature.featureDefinitionId,
      action: feature.action,
      name: feature.name,
      manual: false,
    };

    if (style === "itemised") {
      const number = decision?.calloutSuppressed ? null : nextCallout(feature.rootId);
      const totals = new Map<QuantityUnit, number>();
      addQuantity(totals, quantity);
      items.push({
        key: feature.rootId,
        kind: "item",
        tradeId,
        tradeName: trade?.displayName ?? "Other",
        performer: noteByTrade.get(trade?.id as TradeId)?.performer?.trim() || trade?.performer || "To be determined",
        title: `${label(feature.roomId) === "Whole project" ? "" : `${locationById.get(feature.roomId ?? "")?.roomName ?? ""} `}${feature.name}`.trim().toUpperCase(),
        callout: number === null ? null : `Q-${String(number).padStart(2, "0")}`,
        calloutNumber: number,
        locations: [
          { roomId: feature.roomId, label: label(feature.roomId), lines: [line], quantities: quantityList(totals) },
        ],
        quantities: quantityList(totals),
        note: decision?.note ?? "",
        featureIds: [feature.id],
        rootIds: [feature.rootId],
        manual: false,
        sortOrder: trade?.sortOrder ?? 900,
        presentation: EMPTY_PRESENTATION,
        workItems: [],
      });
      continue;
    }

    /* ---- trade-wide ---- */
    let scope = tradeWide.get(tradeId);
    if (!scope) {
      scope = {
        key: `trade:${tradeId}`,
        kind: "trade-wide",
        tradeId,
        tradeName: trade?.displayName ?? "Other",
        performer: noteByTrade.get(trade?.id as TradeId)?.performer?.trim() || trade?.performer || "To be determined",
        title: `${(trade?.displayName ?? "Other").toUpperCase()} — WHOLE PROJECT`,
        callout: null,
        calloutNumber: null,
        locations: [],
        quantities: [],
        note: noteByTrade.get(tradeId)?.note ?? "",
        featureIds: [],
        rootIds: [],
        manual: false,
        sortOrder: trade?.sortOrder ?? 900,
        presentation: EMPTY_PRESENTATION,
        workItems: [],
      };
      tradeWide.set(tradeId, scope);
    }
    const locationLabel = label(feature.roomId);
    let block = scope.locations.find((b) => b.label === locationLabel);
    if (!block) {
      block = { roomId: feature.roomId, label: locationLabel, lines: [], quantities: [] };
      scope.locations.push(block);
    }
    block.lines.push(line);
    scope.featureIds.push(feature.id);
    scope.rootIds.push(feature.rootId);
  }

  /* ---- manual items ---- */
  for (const manual of input.manualItems) {
    if (onlyTrades && !onlyTrades.has(manual.tradeId)) continue;
    const trade = tradeById(manual.tradeId);
    const line: BidScopeLine = {
      featureId: manual.id,
      rootId: manual.id,
      text: manual.description,
      quantity: null,
      note: "",
      featureDefinitionId: null,
      action: "other",
      name: manual.description || manual.title,
      manual: true,
    };
    if (manual.itemised) {
      const number = nextCallout(manual.id);
      items.push({
        key: manual.id,
        kind: "item",
        tradeId: manual.tradeId,
        tradeName: trade?.displayName ?? "Other",
        performer: noteByTrade.get(trade?.id as TradeId)?.performer?.trim() || trade?.performer || "To be determined",
        title: manual.title.toUpperCase(),
        callout: `Q-${String(number).padStart(2, "0")}`,
        calloutNumber: number,
        locations: [{ roomId: manual.roomId, label: label(manual.roomId), lines: [line], quantities: [] }],
        quantities: [],
        note: "",
        featureIds: [manual.id],
        rootIds: [manual.id],
        manual: true,
        sortOrder: trade?.sortOrder ?? 900,
        presentation: EMPTY_PRESENTATION,
        workItems: [],
      });
    } else {
      let scope = tradeWide.get(manual.tradeId);
      if (!scope) {
        scope = {
          key: `trade:${manual.tradeId}`,
          kind: "trade-wide",
          tradeId: manual.tradeId,
          tradeName: trade?.displayName ?? "Other",
          performer: noteByTrade.get(trade?.id as TradeId)?.performer?.trim() || trade?.performer || "To be determined",
          title: `${(trade?.displayName ?? "Other").toUpperCase()} — WHOLE PROJECT`,
          callout: null,
          calloutNumber: null,
          locations: [],
          quantities: [],
          note: noteByTrade.get(manual.tradeId)?.note ?? "",
          featureIds: [],
          rootIds: [],
          manual: true,
          sortOrder: trade?.sortOrder ?? 900,
          presentation: EMPTY_PRESENTATION,
          workItems: [],
        };
        tradeWide.set(manual.tradeId, scope);
      }
      const locationLabel = label(manual.roomId);
      let block = scope.locations.find((b) => b.label === locationLabel);
      if (!block) {
        block = { roomId: manual.roomId, label: locationLabel, lines: [], quantities: [] };
        scope.locations.push(block);
      }
      block.lines.push(line);
    }
  }

  /* ---- totals ---- */
  for (const scope of tradeWide.values()) {
    const scopeTotals = new Map<QuantityUnit, number>();
    for (const block of scope.locations) {
      const blockTotals = new Map<QuantityUnit, number>();
      for (const line of block.lines) {
        addQuantity(blockTotals, line.quantity);
        addQuantity(scopeTotals, line.quantity);
      }
      block.quantities = quantityList(blockTotals);
    }
    scope.quantities = quantityList(scopeTotals);
    // Locations read in the order a person would walk them.
    scope.locations.sort((a, b) => a.label.localeCompare(b.label));
  }

  const scopes = [...tradeWide.values(), ...items].sort(
    (a, b) =>
      a.sortOrder - b.sortOrder ||
      (a.kind === b.kind ? 0 : a.kind === "trade-wide" ? -1 : 1) ||
      (a.calloutNumber ?? 0) - (b.calloutNumber ?? 0) ||
      a.title.localeCompare(b.title),
  );

  const presentationByKey = new Map((input.presentations ?? []).map((p) => [p.scopeKey, p]));
  for (const scope of scopes) {
    scope.presentation = presentationFor(scope, input.locations, presentationByKey.get(scope.key));
    scope.workItems = workItemsFor(scope, input.locations.length, input.workItemLabels, {
      inclusions: input.inclusionsByFeature,
      specifications: input.specificationsByFeature,
      ownerSupplied: input.ownerSuppliedByFeature,
      materialFeatures: input.materialFeatures,
    });
  }

  const highestCallout = scopes.reduce((m, s) => Math.max(m, s.calloutNumber ?? 0), 0);
  return { scopes, excluded, unassigned, highestCallout };
}

/** The Q numbers this package issued, to be remembered (§68X). */
export function calloutAssignments(model: BidPackageModel): Record<string, number> {
  const out: Record<string, number> = {};
  for (const scope of model.scopes) {
    if (scope.calloutNumber !== null) out[scope.key] = scope.calloutNumber;
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * Has the work changed since somebody priced it?
 * ------------------------------------------------------------------ */

export interface IssuedScope {
  scopeKey: string;
  /** The scope exactly as the package stated it. */
  body: string;
  /** Exact comparison snapshot for packages issued after §94; never backfilled. */
  comparableBody?: string | null;
}

export interface QuoteRelevance {
  scopeKey: string;
  status: "unchanged" | "changed" | "gone" | "unverified";
}

/**
 * WHETHER A QUOTE STILL DESCRIBES THE JOB (spec §68V).
 *
 * A contractor priced a specific list of work. If that work later changes,
 * their number is answering a question nobody is asking any more — and they
 * need to be asked again rather than have the old figure quietly stand.
 *
 * The comparison is against the SCOPES THE QUOTE COVERS, and only those.
 * This is the whole point: moving a sofa, renaming a design or repainting a
 * bedroom changes the project without changing one word of the plumbing
 * scope, and a warning that fires on any change at all is a warning people
 * learn to ignore.
 *
 * Nothing is invalidated or deleted. A quote is what somebody said, and it
 * stays exactly what they said; this only reports whether it still matches.
 */
export function assessQuoteRelevance(
  coveredKeys: readonly string[],
  issued: readonly IssuedScope[],
  current: BidPackageModel,
): QuoteRelevance[] {
  const issuedByKey = new Map(issued.map((scope) => [scope.scopeKey, scope]));
  const currentByKey = new Map(current.scopes.map((scope) => [scope.key, renderComparableBody(scope)]));
  const currentScopeByKey = new Map(current.scopes.map(scope => [scope.key, scope]));

  return coveredKeys.map((scopeKey) => {
    const then = issuedByKey.get(scopeKey);
    const now = currentByKey.get(scopeKey);
    // Never issued in a package, so there is nothing to compare against.
    // Neither "changed" nor "gone" can be evidenced, and asserting either
    // would be inventing a fact — this must be checked FIRST.
    if (then === undefined) return { scopeKey, status: "unchanged" as const };
    // It WAS issued and is no longer in the package: a reverted removal, say.
    if (now === undefined) return { scopeKey, status: "gone" as const };
    if (then.comparableBody != null) return { scopeKey, status: then.comparableBody === now ? "unchanged" as const : "changed" as const };
    // Some consumers already supplied canonical text before the persisted
    // comparison column existed. It contains quantities and remains valid.
    if (then.comparableBody === undefined && then.body === now) return { scopeKey, status: "unchanged" as const };
    // Legacy printable text omitted structured quantities. Matching wording
    // cannot establish that quantities still match; neither is it a change.
    return { scopeKey, status: then.body === renderIssuedScopeBody(currentScopeByKey.get(scopeKey)!) ? "unverified" as const : "changed" as const };
  });
}

/**
 * The comparable text of a scope. Deliberately the SCOPE OF WORK only — not
 * its title, its Q number or its ordering — so renumbering or resorting can
 * never look like a change to the work itself.
 */
export function renderComparableBody(scope: BidScope): string {
  const parts: string[] = [];
  for (const block of [...scope.locations].sort((a, b) => a.label.localeCompare(b.label))) {
    parts.push(block.label);
    for (const line of [...block.lines].sort((a, b) => a.text.localeCompare(b.text))) {
      parts.push(`• ${line.text}${line.quantity ? ` [${line.quantity.unit}:${line.quantity.valueX100}]` : ""}${line.note ? ` — ${line.note}` : ""}`);
    }
  }
  if (scope.note) parts.push(`Notes:\n${scope.note}`);
  return parts.join("\n");
}

/** Exact historical printable-body format, shared with persistence. */
export function renderIssuedScopeBody(scope: BidScope): string {
  const lines: string[] = [];
  for (const block of scope.locations) {
    if (scope.kind === "trade-wide") lines.push(block.label.toUpperCase());
    for (const line of block.lines) lines.push(`• ${line.text}${line.note ? ` — ${line.note}` : ""}`);
    lines.push("");
  }
  if (scope.note) lines.push(scope.note);
  return lines.join("\n").trim();
}

/* ------------------------------------------------------------------ *
 * A selected quote as the current pricing basis (spec §69-6)
 * ------------------------------------------------------------------ */

export interface SelectedQuoteInput {
  quoteId: string;
  contractor: string;
  amountCents: number;
  /** The scope keys this one price covers. */
  coverage: readonly string[];
}

export interface QuoteSubstitution {
  quoteId: string;
  contractor: string;
  quotedCents: number;
  /** What the calculation said the same work would cost. */
  calculatedCents: number;
  scopeKeys: string[];
  scopeTitles: string[];
  /** The features this quote's price claimed (each feature at most once). */
  featureIds: string[];
}

export interface QuotedPricingResult {
  /** The working estimate with selected quotes standing in for their scope. */
  adjustedTotalCents: number;
  /** The untouched calculated total, for the comparison that must survive. */
  calculatedTotalCents: number;
  substitutions: QuoteSubstitution[];
  /**
   * Feature-level comparison is impossible under a combined quote: one
   * number covers several scopes and splitting it would be invented data.
   * Named here so the screen can say so instead of showing blanks.
   */
  combinedCoverageNotes: string[];
}

/**
 * WHAT THE PROJECT CURRENTLY EXPECTS TO PAY (spec §69-6).
 *
 * Four numbers, permanently distinct: the CALCULATED estimate is what
 * Berrier Reno worked out; a QUOTE is what a contractor offered; a SELECTED
 * quote is the price the user has chosen to plan around; ACTUAL is money
 * spent. This function moves the planning basis and touches nothing else —
 * no line is rewritten, no history overwritten, and a selected quote never
 * becomes an expense.
 *
 * Substitution, not addition: the covered scope's calculated lines come out
 * and the quoted figure goes in, so nothing is double-counted. A combined
 * quote is used ONCE across everything it covers.
 *
 * Pure. Same inputs, same numbers.
 */
export function applySelectedQuotes(
  lines: ReadonlyArray<{ featureId: string | null; amountCents: number; archived: boolean }>,
  model: BidPackageModel,
  selected: readonly SelectedQuoteInput[],
): QuotedPricingResult {
  const active = lines.filter((line) => !line.archived);
  const calculatedTotalCents = active.reduce((sum, line) => sum + line.amountCents, 0);

  const scopeByKey = new Map(model.scopes.map((scope) => [scope.key, scope]));
  const substitutions: QuoteSubstitution[] = [];
  const combinedCoverageNotes: string[] = [];
  const claimedFeatures = new Set<string>();
  let adjustment = 0;

  for (const quote of selected) {
    const scopes = quote.coverage
      .map((key) => scopeByKey.get(key))
      .filter((scope): scope is BidScope => scope !== undefined);
    if (scopes.length === 0) continue;

    // Every feature under the covered scopes, each claimed at most once —
    // selection already forbids two selected quotes covering one scope.
    const featureIds = new Set<string>();
    for (const scope of scopes) {
      for (const featureId of scope.featureIds) {
        if (!claimedFeatures.has(featureId)) {
          featureIds.add(featureId);
          claimedFeatures.add(featureId);
        }
      }
    }
    const calculatedCents = active
      .filter((line) => line.featureId !== null && featureIds.has(line.featureId))
      .reduce((sum, line) => sum + line.amountCents, 0);

    adjustment += quote.amountCents - calculatedCents;
    substitutions.push({
      quoteId: quote.quoteId,
      contractor: quote.contractor,
      quotedCents: quote.amountCents,
      calculatedCents,
      scopeKeys: scopes.map((scope) => scope.key),
      scopeTitles: scopes.map((scope) => scope.title),
      featureIds: [...featureIds],
    });
    if (scopes.length > 1) {
      combinedCoverageNotes.push(
        `${quote.contractor}'s price covers ${scopes.length} scopes as one figure — the individual pieces underneath it cannot be compared separately.`,
      );
    }
  }

  return {
    adjustedTotalCents: calculatedTotalCents + adjustment,
    calculatedTotalCents,
    substitutions,
    combinedCoverageNotes,
  };
}

/* ------------------------------------------------------------------ *
 * Presentation (spec §76B–D, G, H)
 * ------------------------------------------------------------------ */

const EMPTY_PRESENTATION: BidScopePresentation = {
  mode: "generalized",
  generated: "",
  custom: null,
  text: "",
  bullets: [],
  groups: [],
  digest: "",
  stale: false,
};

/**
 * A trade bought across the whole house reads as a summary by default; a
 * discrete item is already one line and reads as itself (§76M).
 */
export function defaultPresentationMode(scope: BidScope): BidPresentationMode {
  return scope.kind === "trade-wide" ? "generalized" : "detailed";
}

/**
 * WHAT THE COVERED WORK IS, as one short string (§76H).
 *
 * Deliberately the KINDS of work and nothing else: not quantities, not
 * ordering, not notes. A custom sentence describes what is being done, so
 * adding an interior wall makes it stale and re-measuring a floor does not.
 */
export function scopeDigest(scope: BidScope): string {
  const identities = new Set<string>();
  for (const block of scope.locations) {
    for (const line of block.lines) {
      identities.add(`${line.featureDefinitionId ?? line.name.trim().toLowerCase()}|${line.action}`);
    }
  }
  const canonical = [...identities].sort().join(";");
  // FNV-1a: short, stable, and it never needs to be reversed.
  let hash = 0x811c9dc5;
  for (let i = 0; i < canonical.length; i++) {
    hash ^= canonical.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}

function presentationFor(
  scope: BidScope,
  locations: readonly BidLocationInput[],
  stored: BidPresentation | undefined,
): BidScopePresentation {
  const items: SummaryItem[] = [];
  for (const block of scope.locations) {
    for (const line of block.lines) {
      items.push({
        featureId: line.featureId,
        featureDefinitionId: line.featureDefinitionId,
        name: line.name,
        action: line.action,
        roomId: block.roomId,
        quantityX100: line.quantity?.valueX100 ?? null,
        quantityUnit: line.quantity?.unit ?? null,
        manual: line.manual,
      });
    }
  }
  const summary = summarizeScope({
    tradeId: scope.tradeId,
    items,
    rooms: locations.map((location) => ({
      roomId: location.roomId,
      roomName: location.roomName,
      floorName: location.floorName,
    })),
  });

  const digest = scopeDigest(scope);
  const mode = stored?.mode ?? defaultPresentationMode(scope);
  const custom = stored?.customText?.trim() ? stored.customText.trim() : null;
  // A custom wording is only ever REPORTED as stale — never rewritten,
  // never discarded, never quietly replaced by the generated one (§76H).
  const stale =
    mode === "custom" && custom !== null && stored?.customDigest != null && stored.customDigest !== digest;

  // The generated PARAGRAPH and the manual BULLETS are kept apart: the
  // paragraph is what a mode swaps out, and a manual item is somebody own
  // words that survives every mode (spec §76Q).
  const paragraph = summary.sentences.join(" ");
  return {
    mode,
    generated: paragraph,
    custom,
    text: mode === "detailed" ? "" : mode === "custom" && custom ? custom : paragraph,
    bullets: mode === "detailed" ? [] : summary.bullets,
    groups: summary.groups,
    digest,
    stale,
  };
}

/**
 * The scope's work items, by the SAME grouping the Renovation Budget uses
 * (spec §79B): §78's deriveManagementLines over this scope's own lines.
 * Amounts are deliberately zero — a bid sheet never carries the owner's
 * figures; the identity, wording, counts and membership are the point.
 */
function workItemsFor(
  scope: BidScope,
  totalRooms: number,
  labels: ReadonlyMap<string, string> | undefined,
  context: WorkItemContext,
): BidWorkItem[] {
  const details: CategoryDetailLine[] = [];
  const seen = new Set<string>();
  for (const block of scope.locations) {
    for (const line of block.lines) {
      if (seen.has(line.featureId)) continue; // one detail per feature
      seen.add(line.featureId);
      details.push({
        featureId: line.featureId,
        featureDefinitionId: line.featureDefinitionId,
        action: line.action,
        roomId: block.roomId,
        name: line.manual ? line.name : line.text,
        locationLabel: block.label,
        calculatedCents: 0,
        actualCents: 0,
        materialCents: 0,
        laborCents: 0,
        otherCostCents: 0,
        unclassifiedCents: 0,
      });
    }
  }
  const items = deriveManagementLines(details, totalRooms, labels).map((item) => {
    const featureIds = item.details.map((d) => d.featureId).filter((id): id is string => id !== null);
    return {
      key: item.key,
      label: item.label,
      generatedLabel: item.generatedLabel,
      featureCount: item.featureCount,
      featureIds,
      ...gather(featureIds, scope, context),
      manual: item.key === "manual-lines" || item.details.some((d) => d.featureDefinitionId === null),
      explicit: item.details.some((d) => d.featureDefinitionId !== null && ALWAYS_EXPLICIT.has(d.featureDefinitionId)),
    };
  });

  /* A trade bought as a whole prices as ONE work package (spec §79E): a
   * plumber bids "Plumbing renovation per plan", never toilet-by-toilet.
   * Work that must never be generalised away (§76F) and manual items stay
   * their own lines, exactly as on the spreadsheet. */
  /*
   * PAINTING IS THE EXCEPTION (spec §82-27): wall paint and ceiling paint
   * are measured on different surfaces and priced differently, so they
   * stay two lines. §78 has already merged every room's walls into one
   * identity and every room's ceilings into another — which is exactly
   * the pair a painter wants to price.
   */
  if (scope.tradeId !== "painting-interior" && AGGREGATE[scope.tradeId] !== undefined) {
    const ordinary = items.filter((item) => !item.manual && !item.explicit);
    if (ordinary.length >= 2) {
      const key = `trade:${scope.tradeId}:general`;
      const generatedLabel = AGGREGATE_BID_LABEL[scope.tradeId] ?? `${scope.tradeName} renovation per plan`;
      const mergedIds = ordinary.flatMap((item) => item.featureIds);
      const merged: BidWorkItem = {
        key,
        generatedLabel,
        label: labels?.get(key) ?? generatedLabel,
        featureCount: ordinary.reduce((s, item) => s + item.featureCount, 0),
        featureIds: mergedIds,
        ...gather(mergedIds, scope, context),
      };
      return [merged, ...items.filter((item) => item.manual || item.explicit).map(strip)];
    }
  }
  return items.map(strip);
}

const strip = (item: BidWorkItem & { manual?: boolean; explicit?: boolean }): BidWorkItem => ({
  key: item.key,
  label: item.label,
  generatedLabel: item.generatedLabel,
  featureCount: item.featureCount,
  featureIds: item.featureIds,
  quantities: item.quantities,
  inclusions: item.inclusions,
  specifications: item.specifications,
  ownerSupplied: item.ownerSupplied,
  excludedMaterial: item.excludedMaterial,
  notes: item.notes,
  members: item.members,
});

/** What the project knows about a work item's own features (spec §80-9). */
interface WorkItemContext {
  inclusions?: ReadonlyMap<string, readonly string[]>;
  specifications?: ReadonlyMap<string, readonly string[]>;
  ownerSupplied?: ReadonlyMap<string, readonly string[]>;
  materialFeatures?: ReadonlySet<string>;
}

/**
 * Everything a contractor is told about one work item, gathered from the
 * features it covers. Quantities aggregate ONLY when every feature states
 * one in the same unit (spec §80-17) — a partial sum printed as the total
 * would be a lie about the size of the job.
 */
function gather(
  featureIds: readonly string[],
  scope: BidScope,
  context: WorkItemContext,
): Pick<BidWorkItem, "quantities" | "inclusions" | "specifications" | "ownerSupplied" | "excludedMaterial" | "notes" | "members"> {
  const memberIds = new Set(featureIds);
  const lines = scope.locations.flatMap((block) => block.lines).filter((line) => memberIds.has(line.featureId));
  const units = new Set(lines.map((line) => line.quantity?.unit ?? null));
  const stated = lines.filter((line) => line.quantity !== null);
  const quantities: BidQuantity[] =
    lines.length > 0 && stated.length === lines.length && units.size === 1 && !units.has(null)
      ? [{ unit: stated[0].quantity!.unit, valueX100: stated.reduce((s, l) => s + l.quantity!.valueX100, 0) }]
      : [];
  const collect = (map?: ReadonlyMap<string, readonly string[]>): string[] => {
    if (!map) return [];
    const out: string[] = [];
    for (const id of featureIds) for (const value of map.get(id) ?? []) out.push(value);
    return out;
  };
  const notes = lines.map((line) => line.note).filter((note) => note.trim() !== "");
  /*
   * §80-12: an excluded material component means one of two things, and
   * they are not interchangeable. If the item still buys material, the
   * exclusion is simply not in the price. Only when the item buys NO
   * material at all is the contractor pricing labour on somebody else's
   * material — and only then may the sheet say so.
   */
  const inclusions = collect(context.inclusions);
  const included = new Set(inclusions.map((name) => name.toLowerCase()));
  // A work item can span features that made opposite calls: one roof takes
  // the decking allowance, another does not. Where the item DOES buy the
  // thing, it is included — never reported as excluded in the same breath.
  const excluded = collect(context.ownerSupplied).filter((name) => !included.has(name.toLowerCase()));
  const buysMaterial = featureIds.some((id) => context.materialFeatures?.has(id) ?? false);
  const members = lines.map((line) => ({
    featureId: line.featureId,
    label: line.manual ? line.name : line.text,
    specifications: [...(context.specifications?.get(line.featureId) ?? [])],
    inclusions: [...(context.inclusions?.get(line.featureId) ?? [])],
    quantities: line.quantity ? [line.quantity] : [],
  }));
  return {
    quantities,
    inclusions,
    specifications: collect(context.specifications),
    ownerSupplied: buysMaterial ? [] : excluded,
    excludedMaterial: buysMaterial ? excluded : [],
    notes: [...new Set(notes)],
    members,
  };
}

/** Spreadsheet-style wording for a whole-trade work package (spec §79U). */
const AGGREGATE_BID_LABEL: Partial<Record<TradeId, string>> = {
  demolition: "Interior demolition and disposal",
  plumbing: "Plumbing renovation per plan",
  electrical: "Electrical modifications per plan",
  "painting-interior": "Paint interior surfaces per plan",
  drywall: "Drywall / tape / bed",
  tile: "Tile work per plan",
  cleaning: "Cleanup and disposal",
};

/* ------------------------------------------------------------------ *
 * Material / labor / total quote arithmetic (spec §79G/J)
 * ------------------------------------------------------------------ */

export interface QuoteAmounts {
  materialCents: Cents | null;
  laborCents: Cents | null;
  totalCents: Cents;
}

/**
 * The consistency rules, in one place (spec §79G/J):
 *   material + labor both stated  → total IS their sum, to the cent;
 *   one side stated               → total is that side (the other is
 *                                   UNKNOWN — blank, never zero);
 *   total only                    → kept as authoritative; no split is
 *                                   ever invented, no 50/50, nothing.
 */
export function resolveQuoteAmounts(
  materialCents: Cents | null,
  laborCents: Cents | null,
  totalCents: Cents | null,
): { ok: true; amounts: QuoteAmounts } | { ok: false; problem: string } {
  if (materialCents !== null && laborCents !== null) {
    const sum = materialCents + laborCents;
    if (totalCents !== null && totalCents !== sum) {
      return { ok: false, problem: "Material + labor must equal the total to the cent — fix one of the three." };
    }
    return { ok: true, amounts: { materialCents, laborCents, totalCents: sum } };
  }
  if (materialCents !== null && laborCents === null) {
    if (totalCents !== null && totalCents !== materialCents) {
      // Material stated and a LARGER total: the difference is not
      // assumed to be labor — nothing is inferred. Keep both honest.
      return { ok: true, amounts: { materialCents, laborCents: null, totalCents } };
    }
    return { ok: true, amounts: { materialCents, laborCents: null, totalCents: materialCents } };
  }
  if (laborCents !== null && materialCents === null) {
    if (totalCents !== null && totalCents !== laborCents) {
      return { ok: true, amounts: { materialCents: null, laborCents, totalCents } };
    }
    return { ok: true, amounts: { materialCents: null, laborCents, totalCents: laborCents } };
  }
  if (totalCents === null) {
    return { ok: false, problem: "A quote needs a total, or a material/labor pair that makes one." };
  }
  return { ok: true, amounts: { materialCents: null, laborCents: null, totalCents } };
}

export interface QuoteBreakdownLine extends QuoteAmounts {
  groupKey: string;
  label: string;
  /** Who supplies the material for this work (spec §79L); null = unstated. */
  materialResponsibility: "contractor" | "owner" | "mixed" | null;
}

/**
 * Roll a breakdown up (spec §79H/I): totals always sum; a material or
 * labor rollup exists only when EVERY line states that side — a partial
 * sum would read as the whole and misstate what the contractor said.
 */
export function rollUpQuoteLines(lines: readonly QuoteAmounts[]): QuoteAmounts | null {
  if (lines.length === 0) return null;
  const totalCents = lines.reduce((s, l) => s + l.totalCents, 0);
  const materialCents = lines.every((l) => l.materialCents !== null)
    ? lines.reduce((s, l) => s + (l.materialCents ?? 0), 0)
    : null;
  const laborCents = lines.every((l) => l.laborCents !== null)
    ? lines.reduce((s, l) => s + (l.laborCents ?? 0), 0)
    : null;
  return { materialCents, laborCents, totalCents };
}
